import { useEffect } from 'react';
import { isSessionExpired } from '../auth/sessionExpiry';

/**
 * useKeyboardShortcuts — suscribe handlers de atajos de teclado al documento.
 *
 * Ignora eventos que vengan de un input/textarea/select/contenteditable para
 * no pisar escritura del usuario.
 *
 * @param {Object<string, (e: KeyboardEvent) => void>} shortcuts
 *        Mapa de tecla (en minúscula) → handler. Ejemplo: `{ a: handleApprove, r: handleReject }`.
 * @param {Object} options
 * @param {boolean} options.enabled — si false, no suscribe. Default true.
 */
export const useKeyboardShortcuts = (shortcuts, { enabled = true } = {}) => {
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      // Con la sesion vencida hay un aviso bloqueante arriba, pero estos handlers viven en
      // `document` y su guarda no excluye <button> — que es justo donde el aviso deja el foco.
      // Sin esto, apretar "a" por reflejo ejecutaba la aprobacion masiva DE VERDAD mientras el
      // usuario miraba un cartel diciendole que su sesion no servia (#168).
      if (isSessionExpired()) return;
      const tag = (e.target?.tagName || '').toUpperCase();
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return; // no interferir con shortcuts nativos
      const handler = shortcuts[e.key?.toLowerCase()];
      if (handler) {
        handler(e);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [shortcuts, enabled]);
};

export default useKeyboardShortcuts;
